import type { MpObstacleState } from "../../../lib/multiplayer/protocol";
import { mpDrawObstacle, mpNormToCanvas, MP_REF_H, MP_REF_W } from "../../../lib/multiplayer/normal-sim";
import { highwayWorldToScreen } from "../highway-mode";
import type { MpInterpolatedFrame } from "./snapshot-buffer";
import { drawCosmeticOverlaysForIds, cosmeticIdsHideBaseCursor } from "../cursor-cosmetics";

export type RemotePlayer = {
  id: string;
  name: string;
  renderX: number;
  renderY: number;
  alive: boolean;
  isSpectator: boolean;
  trailEnabled: boolean;
  trailColor: string;
  equippedCosmeticIds: string[];
};

type TrailPoint = { nx: number; ny: number; life: number };

const TRAIL_MAX = 14;
const TRAIL_LIFE = 12;

function playerToCanvas(
  nx: number,
  ny: number,
  cw: number,
  ch: number,
  isHighway: boolean,
): { x: number; y: number } {
  if (isHighway) {
    return highwayWorldToScreen(nx * MP_REF_W, ny * MP_REF_H, cw, ch, MP_REF_W, MP_REF_H);
  }
  return mpNormToCanvas(nx, ny, cw, ch);
}

export class RemotePlayerStore {
  private players = new Map<string, RemotePlayer>();
  private names = new Map<string, string>();
  private cosmetics = new Map<string, string[]>();
  private trails = new Map<string, TrailPoint[]>();
  obstacles: MpObstacleState[] = [];
  serverScore = 0;

  setPlayerName(id: string, name: string): void {
    this.names.set(id, name);
    const p = this.players.get(id);
    if (p) p.name = name;
  }

  setPlayerCosmetics(id: string, ids: string[]): void {
    this.cosmetics.set(id, ids.slice(0, 16));
    const p = this.players.get(id);
    if (p) p.equippedCosmeticIds = [...ids.slice(0, 16)];
  }

  /** Apply interpolated frame from snapshot buffer (called each rAF, not on WS). */
  applyFrame(frame: MpInterpolatedFrame, localId: string | null, dt: number): void {
    this.serverScore = frame.score;
    this.obstacles = frame.obstacles;
    const fs = Math.min(3, dt * 60);

    const seen = new Set<string>();
    for (const sp of frame.players) {
      if (sp.id === localId) continue;
      seen.add(sp.id);
      let p = this.players.get(sp.id);
      if (!p) {
        p = {
          id: sp.id,
          name: this.names.get(sp.id) ?? "Player",
          renderX: sp.x,
          renderY: sp.y,
          alive: sp.alive,
          isSpectator: sp.isSpectator,
          trailEnabled: sp.trailEnabled,
          trailColor: sp.trailColor,
          equippedCosmeticIds: this.cosmetics.get(sp.id) ?? [],
        };
        this.players.set(sp.id, p);
      } else {
        const lerpT = Math.min(1, fs * 0.85);
        const dx = sp.x - p.renderX;
        const dy = sp.y - p.renderY;
        if (Math.hypot(dx, dy) > 0.06) {
          p.renderX = sp.x;
          p.renderY = sp.y;
        } else {
          p.renderX += dx * lerpT;
          p.renderY += dy * lerpT;
        }
        p.alive = sp.alive;
        p.isSpectator = sp.isSpectator;
        p.trailEnabled = sp.trailEnabled;
        p.trailColor = sp.trailColor;
        p.name = this.names.get(sp.id) ?? p.name;
        p.equippedCosmeticIds = this.cosmetics.get(sp.id) ?? p.equippedCosmeticIds;
      }

      if (sp.trailEnabled && sp.alive && !sp.isSpectator) {
        let pts = this.trails.get(sp.id);
        if (!pts) {
          pts = [];
          this.trails.set(sp.id, pts);
        }
        pts.unshift({ nx: sp.x, ny: sp.y, life: TRAIL_LIFE });
        if (pts.length > TRAIL_MAX) pts.pop();
        for (const t of pts) t.life -= fs;
        this.trails.set(sp.id, pts.filter((t) => t.life > 0));
      } else {
        this.trails.delete(sp.id);
      }
    }
    for (const id of [...this.players.keys()]) {
      if (!seen.has(id)) {
        this.players.delete(id);
        this.trails.delete(id);
      }
    }
  }

  syncNames(names: Map<string, string>): void {
    this.names = new Map(names);
    for (const p of this.players.values()) {
      p.name = names.get(p.id) ?? p.name;
    }
  }

  syncCosmetics(cosmetics: Map<string, string[]>): void {
    this.cosmetics = new Map(cosmetics);
    for (const p of this.players.values()) {
      p.equippedCosmeticIds = cosmetics.get(p.id) ?? p.equippedCosmeticIds;
    }
  }

  getRemotes(_localId: string | null): RemotePlayer[] {
    return [...this.players.values()];
  }

  drawRemoteTrails(
    ctx: CanvasRenderingContext2D,
    cw: number,
    ch: number,
    localId: string | null,
    isHighway = false,
  ): void {
    const ls = Math.min(cw, ch) / 600;
    for (const p of this.getRemotes(localId)) {
      if (!p.trailEnabled) continue;
      const pts = this.trails.get(p.id);
      if (!pts || pts.length < 2) continue;

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        const alpha = Math.max(0, a.life / TRAIL_LIFE);
        const pa = playerToCanvas(a.nx, a.ny, cw, ch, isHighway);
        const pb = playerToCanvas(b.nx, b.ny, cw, ch, isHighway);
        ctx.strokeStyle = p.trailColor;
        ctx.globalAlpha = alpha * 0.45;
        ctx.lineWidth = Math.max(1 * ls, alpha * 4 * ls);
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  drawRemoteCursors(
    ctx: CanvasRenderingContext2D,
    cw: number,
    ch: number,
    localId: string | null,
    cursorRadiusPx: number,
    isHighway = false,
  ): void {
    for (const p of this.getRemotes(localId)) {
      if (p.isSpectator && !p.alive) continue;
      const { x, y } = playerToCanvas(p.renderX, p.renderY, cw, ch, isHighway);
      ctx.save();
      if (!cosmeticIdsHideBaseCursor(p.equippedCosmeticIds)) {
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(x, y, cursorRadiusPx, 0, Math.PI * 2);
        ctx.fill();
      }
      drawCosmeticOverlaysForIds(ctx, x, y, cursorRadiusPx, p.equippedCosmeticIds);

      ctx.font = `${Math.max(10, Math.round(cursorRadiusPx * 2.2))}px "Press Start 2P", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.85)";
      ctx.shadowBlur = 4;
      ctx.fillText(p.name, x, y - cursorRadiusPx - 4);
      ctx.restore();
    }
  }

  drawObstacles(ctx: CanvasRenderingContext2D, cw: number, ch: number, color: string): void {
    for (const o of this.obstacles) {
      mpDrawObstacle(ctx, o, cw, ch, color);
    }
  }

  clear(): void {
    this.players.clear();
    this.names.clear();
    this.cosmetics.clear();
    this.trails.clear();
    this.obstacles = [];
    this.serverScore = 0;
  }
}

export const remotePlayers = new RemotePlayerStore();
